/**
 * ROOF ROUND E3 — the roof's ACTUAL DERIVED UNDERSIDE, as one shared evaluator
 * (docs/MODELER.md "E3 SCOPE ADDITION" — the E1 reveal fix). E1 stepped the eave
 * zone's underside up past the bearing line; E2's tails eaves thin further to the
 * decking read. Anything that needs the underside — the roof's OWN mesh
 * (generate3d) and walls-attach-to-roof clipping (wall/generate3d#targetSurfaces) —
 * must read the SAME zone split from HERE, never a re-derivation, or the two drift
 * and a wall attached under a thinned eave stops at the wrong plane (the screenshot
 * reveal: a gable wall under a rake overhang with the eave crop on — most of its
 * thickness lies OUTSIDE the bearing line, clipped at the full-depth plane that no
 * longer exists there).
 *
 * The split is GEOMETRIC, not per-layer: body zone (inside the bearing polygon) at
 * the solved bearing plane, eave zone raised plumb by the crop — whichever share of
 * a wall's thickness lies under each zone ends at that zone's plane, and
 * deriveAttachedTop's existing piece-step machinery draws the plumb face between
 * them. This deliberately SOFTENED E1's byte-identical-clipping invariant to:
 * identical wherever the wall lies under the BODY zone (an uncropped face still
 * yields exactly the pre-E1 single surface).
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import { clipperReady, differenceClosed, intersectClosed, signedArea } from '../../clipper'
import { assemblyZonesFt, deckingFt, spanOnBearingLine } from './framing'
import { eaveCropFtFor, flatSoffitLevelPolys, flatSoffitZ, roofFaceIsFlatSoffit } from './soffit'
import { solveRoofForElement, type ResolvedRoofSolve, type RoofFace } from './solve'

// E1's crop read moved to soffit.ts in the FLAT SOFFIT + CORBELS round (framing.ts
// needs it too, and framing ↔ underside would cycle) — re-exported for its readers
export { eaveCropFtFor } from './soffit'

const ZONE_AREA_EPS = 1e-6

export interface RoofFaceUnderside {
  /** the eave-zone rise, perpendicular-to-slope feet (0 = uncropped face) */
  cropFt: number
  /** the same rise converted plumb by the face's √(1+m²) */
  cropDv: number
  /** face ∩ bearing polygon — underside at the solved bearing plane */
  bodyRings: Pt2[][]
  /** face − bearing polygon — underside raised by cropDv (on a flat-soffit face: the
   *  SLOPED remainder of the eave zone, e.g. a rake band past its return) */
  eaveRings: Pt2[][]
  /** FLAT SOFFIT + CORBELS round: the LEVEL part of the eave zone — the flat soffit
   *  and its gable returns — at the one elevation `levelZ` (empty on a sloped soffit) */
  levelRings: Pt2[][]
  /** the flat soffit's absolute elevation (NaN when levelRings is empty) */
  levelZ: number
  /** true when the face actually splits (an eave zone exists and the crop is live, or
   *  a flat soffit exists) */
  cropped: boolean
}

/**
 * OPEN eaves (the eave-styling round, 2026-08-10 — instance `eaveMode`): the eave
 * zone's mass thins to the decking + finishes on EVERY face (the E2 tails
 * treatment, generalized) and the eave-zone structure is instead EXPRESSED as real
 * members by framing.ts, visible in 3D. Flat roofs stay boxed — their open-joist
 * expression is deferred with flat framing.
 */
export function roofEavesOpen(el: ResolvedElement, solved: ResolvedRoofSolve): boolean {
  return el.params.eaveMode === 'open' && !solved.flat
}

/**
 * THE underside evaluator for one solved face: resolves the crop (E2 tails and the
 * OPEN eave mode supersede the E1 depth — the eave thins to decking + above-core
 * finishes) and splits the face ring at the bearing polygon. Exception-proof and
 * clipper-guarded (the Forsyth lesson): any failure degrades to "uncropped", never
 * a throw.
 */
export function roofFaceUnderside(el: ResolvedElement, solved: ResolvedRoofSolve, f: RoofFace): RoofFaceUnderside {
  const t = el.assemblyThicknessFt
  const zones = assemblyZonesFt(el)
  // FLAT SOFFIT + CORBELS round: a flat soffit keeps the BOXED eave section (the E1
  // crop is its fascia depth) — its tails are corbels under the soffit, not members
  // hanging from thinned decking, and an open eave mode has no meaning under it
  const flat = roofFaceIsFlatSoffit(solved, f)
  const cropFt = !flat && (f.tails || roofEavesOpen(el, solved))
    ? Math.max(0, Math.min(zones.belowFt + zones.coreFt - deckingFt(el), t - 1 / 48))
    : eaveCropFtFor(el, f.eaveDepthIn)
  const cropDv = cropFt * Math.sqrt(1 + f.m * f.m)
  const levelZ = flat ? (flatSoffitZ(el, solved, f) ?? NaN) : NaN
  let eaveRings: Pt2[][] = []
  let bodyRings: Pt2[][] = []
  let levelRings: Pt2[][] = []
  // TEAR r4 (2026-08-12): a FLYING RAKE strip is pure overhang zone — it hangs
  // over the LOWER mass's bearing footprint, so the geometric face∩bearing
  // split below would misread that stretch as body (full depth, uncropped —
  // Matt's "the depth reduction isn't extending onto the overlap")
  if (f.flying) {
    return { cropFt, cropDv, bodyRings, eaveRings: cropDv > 1e-9 ? [f.ring.map((p) => [p[0], p[1]] as Pt2)] : [], levelRings: [], levelZ: NaN, cropped: cropDv > 1e-9 }
  }
  const wantLevel = flat && Number.isFinite(levelZ)
  if ((cropDv > 1e-9 || wantLevel) && solved.ring.length >= 3 && clipperReady()) {
    try {
      // outer rings only (CCW-positive) — the zones are slivers of a simple ring,
      // holes can't arise; drop any degenerate output defensively
      const keep = (rings: Pt2[][]) => rings.filter((r) => r.length >= 3 && signedArea(r) > ZONE_AREA_EPS)
      eaveRings = keep(differenceClosed([f.ring], [solved.ring]))
      if (eaveRings.length) bodyRings = keep(intersectClosed([f.ring], [solved.ring]))
      // FLAT SOFFIT: carve the LEVEL region (the eave band + any gable returns) out of
      // the eave zone; what remains of it (a rake band past its return) stays sloped
      if (wantLevel && eaveRings.length) {
        const polys = flatSoffitLevelPolys(el, solved, f)
        if (polys.length) {
          levelRings = keep(intersectClosed(eaveRings, polys))
          if (levelRings.length) eaveRings = keep(differenceClosed(eaveRings, levelRings))
        }
      }
    } catch {
      eaveRings = []; bodyRings = []; levelRings = []
    }
  }
  const cropped = levelRings.length > 0 || (cropDv > 1e-9 && eaveRings.length > 0)
  return { cropFt, cropDv, bodyRings, eaveRings, levelRings, levelZ: levelRings.length ? levelZ : NaN, cropped }
}

/**
 * PLATE-HEIGHT round (docs/MODELER.md "PLANNED — PLATFORM FRAMING + PLATE HEIGHT
 * CORRECTIONS", 2026-08-13): the WALL-FRAMING plate rule's view of a roof attach
 * target — one entry per solved face, carrying the face's MAIN BODY plane
 * (`zAt`, UNCROPPED — the E1 eave crop raises the MASS underside in the eave
 * zone, but the body framing keeps riding the true plane, and the plate follows
 * the framing, never the cropped eave underside) plus everything the plate rule
 * needs: the plan gradient (uphill/m), the BEARING HEIGHT (the plate line the
 * birdsmouth seats on — `zAt` evaluated ON the bearing line), the bearing
 * edge's extent (for the "this wall hosts the bearing line" proximity test),
 * and the plumb rise from the mass underside to the roof's own FRAMING
 * underside (the below-core layers, e.g. a gyp ceiling on the rafters).
 */
export interface RoofPlateFace {
  ring: Pt2[]
  /** the face's solved bearing (mass-underside) plane — uncropped */
  zAt: (p: Pt2) => number
  /** unit plan direction of ascent (the bearing edge's inward normal) */
  uphill: Pt2
  /** rise per plan foot */
  m: number
  /** z of the plane ON the bearing line — the plate line the birdsmouth seats on */
  bearingZ: number
  /** a point on the bearing line */
  origin: Pt2
  /** axis along the bearing line (perpendicular to uphill) */
  eAxis: Pt2
  /** the bearing polygon's extent on this face's bearing line along eAxis
   *  (null = the polygon never reaches the line — degenerate) */
  eSpan: [number, number] | null
  /** plumb rise from the mass underside to the roof FRAMING underside */
  framingRiseDv: number
}

export function roofPlateFaces(el: ResolvedElement): RoofPlateFace[] {
  const solved = solveRoofForElement(el)
  if (!solved || !solved.faces.length) return []
  const zones = assemblyZonesFt(el)
  const out: RoofPlateFace[] = []
  for (const f of solved.faces) {
    const sq = Math.sqrt(1 + f.m * f.m)
    const e: Pt2 = [-f.uphill[1], f.uphill[0]]
    out.push({
      ring: f.ring,
      zAt: f.zAt,
      uphill: f.uphill,
      m: f.m,
      bearingZ: f.zAt(f.origin),
      origin: f.origin,
      eAxis: e,
      eSpan: spanOnBearingLine(solved.ring, f.origin, f.uphill, e),
      framingRiseDv: zones.belowFt * sq,
    })
  }
  return out
}

/**
 * Walls-attach clip surfaces for a roof target: the derived underside, zone-split.
 * An uncropped face yields EXACTLY the pre-E3 single surface ([face ring] at the
 * bearing plane), so body-zone clipping stays byte-identical.
 */
export function roofUndersideSurfaces(el: ResolvedElement): { rings: Pt2[][]; zAt: (p: Pt2) => number }[] {
  const solved = solveRoofForElement(el)
  if (!solved || !solved.faces.length) return []
  const out: { rings: Pt2[][]; zAt: (p: Pt2) => number }[] = []
  for (const f of solved.faces) {
    const u = roofFaceUnderside(el, solved, f)
    if (!u.cropped) {
      out.push({ rings: [f.ring], zAt: f.zAt })
      continue
    }
    if (u.bodyRings.length) out.push({ rings: u.bodyRings, zAt: f.zAt })
    const rise = u.cropDv
    if (u.eaveRings.length) out.push({ rings: u.eaveRings, zAt: (p: Pt2) => f.zAt(p) + rise })
    // FLAT SOFFIT + CORBELS round: a wall under the flat soffit stops AT the soffit
    if (u.levelRings.length) {
      const z = u.levelZ
      out.push({ rings: u.levelRings, zAt: () => z })
    }
  }
  return out
}
